import { thumbHashToDataURL } from "thumbhash";

/**
 * How many decoded placeholders are kept. A data URL is 1–2 KB; the catalogue
 * has 3,791 clips, and a list scrolled end to end would otherwise keep every
 * one of them alive. The screens on either side of the viewport fit easily.
 */
const LIMIT = 512;

type Decode = (bytes: Uint8Array) => string;

function bytesOf(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * A memoised ThumbHash → PNG data URL decoder (D121). Decoding is synchronous
 * and cheap, but not free once per row per render in a 3,001-row list (T67),
 * so each hash is decoded once and kept, least recently used first out.
 *
 * `null` in (the clip has no poster, a real state) and a hash that does not
 * decode both give `null`: the caller paints the plain plate.
 */
export function createThumbhashCache(
  decode: Decode = thumbHashToDataURL,
  limit = LIMIT
) {
  const cache = new Map<string, string | null>();

  return function dataUrl(base64: string | null): string | null {
    if (!base64) {
      return null;
    }
    const hit = cache.get(base64);
    if (hit !== undefined) {
      // Re-insert, so Map order is recency order.
      cache.delete(base64);
      cache.set(base64, hit);
      return hit;
    }
    let url: string | null;
    try {
      url = decode(bytesOf(base64));
    } catch {
      url = null;
    }
    cache.set(base64, url);
    if (cache.size > limit) {
      const oldest = cache.keys().next().value;
      if (oldest !== undefined) {
        cache.delete(oldest);
      }
    }
    return url;
  };
}

/** The app's one cache. */
export const thumbhashDataUrl = createThumbhashCache();
